/**
 * AWS Cognito Authentication Service
 *
 * Handles all authentication operations through a secure backend proxy architecture.
 * This service implements critical security patterns to protect sensitive user data
 * and prevent common authentication vulnerabilities.
 *
 * Key architectural decisions:
 * - Backend proxy pattern instead of direct Cognito SDK
 *   WHY: Prevents exposure of Cognito app client ID, user pool details, and AWS configuration.
 *   Direct SDK usage would expose these in client-side code, allowing attackers to bypass
 *   rate limiting, attempt user enumeration, or target the Cognito infrastructure directly.
 *
 * - Basic Auth for credentials transmission (username:password base64)
 *   WHY: Separates credentials from request body, preventing accidental logging of passwords
 *   in request body logs. Backend immediately validates and discards after Cognito API call.
 *   Alternative (credentials in body) risks password exposure in application logs.
 *
 * - Token storage in localStorage vs sessionStorage
 *   WHY: Enables seamless multi-tab experience and session persistence across browser restarts.
 *   Trade-off: XSS vulnerability risk mitigated by short token lifespan (15 min) and CSP headers.
 *   Refresh tokens stored as HttpOnly cookies (not accessible via JavaScript).
 *
 * Security considerations:
 * - NEVER log authentication credentials (passwords, MFA codes, tokens)
 * - All errors mapped to user-friendly messages to prevent information leakage
 * - MFA enforcement for high-value accounts handled server-side
 * - Session validation includes token expiry and revocation checks
 *
 * AWS Cognito challenge flows handled:
 * - SMS_MFA / SOFTWARE_TOKEN_MFA: Multi-factor authentication required
 * - NEW_PASSWORD_REQUIRED: Admin-created users must set password
 * - FORCE_CHANGE_PASSWORD: Password expired per policy
 * - CUSTOM_CHALLENGE: Custom auth flow (future extensibility)
 *
 * @module apiAuth
 * @see {@link .claude/security-rules.md} - Security standards and authentication patterns
 * @see {@link docs/08_AUTHENTICATION_FLOWS.md} - Detailed auth flow documentation
 * @see {@link src/features/authentication/CLAUDE.md} - Frontend auth integration patterns
 */

import { getData } from './apiFetch';
import type {
  CognitoAuthResponse,
  SessionVerification,
  MFASetupResponse,
  MFAVerification,
  SignupResponse,
  PasswordResetResponse,
  AttributeChangeResponse,
  UserResponse,
} from '../types/api';
import type {
  AuthenticateParams,
  SignupParams,
  ConfirmSignupParams,
  ResendSignupCodeParams,
  ForgotPasswordParams,
  ConfirmForgotPasswordParams,
  ChangePasswordParams,
  SetupMFAParams,
  VerifyMFAParams,
  DisableMFAParams,
  UpdateUserAttributesParams,
  ChangeEmailParams,
  ChangePhoneParams,
  ConfirmAttributeChangeParams,
  ResendAttributeCodeParams,
  GlobalSignOutParams,
} from '../types/api/parameters';

// ============================================================================
// Session Management
// ============================================================================

/**
 * Verifies the current session validity with the backend.
 *
 * WHY this exists: Client-side JWT validation can be bypassed or manipulated.
 * Backend verification ensures token hasn't been revoked, user hasn't been
 * disabled, and all security policies are still met (e.g., MFA still enabled).
 *
 * Security rationale:
 * - Quick return on missing token prevents unnecessary API call
 * - Backend validates token signature, expiry, and revocation status
 * - Returns user context for authorization decisions
 *
 * @returns User session data if valid, false if no token or invalid
 * @security Never returns detailed error reasons to prevent session probing attacks
 */
export async function verifySession(): Promise<SessionVerification | false> {
  const token = localStorage.getItem('accessToken');

  // WHY early return: Prevents unnecessary API call and potential error logging
  // when user is clearly not authenticated. This also prevents timing attacks
  // that could differentiate between "no token" and "invalid token" states.
  if (!token) return false;

  const config = {
    method: 'get' as const,
    path: 'auth/verify',
  };

  const data = await getData<SessionVerification>({ ...config });

  return data;
}

// ============================================================================
// Authentication Flows
// ============================================================================

/**
 * Authenticates user supporting multiple AWS Cognito challenge flows.
 *
 * WHY this complexity: AWS Cognito's authentication isn't just username/password.
 * It implements a challenge-response system where initial auth may trigger various
 * challenges (MFA, password reset, etc.) that must be handled in subsequent calls.
 * This single endpoint handles all challenge types to simplify frontend state management.
 *
 * Security architecture:
 * - Basic Auth header for credentials instead of body
 *   WHY: Prevents password from appearing in request body logs. Many logging systems
 *   automatically redact Authorization headers but not body content.
 * - Backend handles all Cognito SDK interactions
 *   WHY: Hides AWS configuration, prevents client-side token manipulation, enables
 *   server-side rate limiting and anomaly detection.
 *
 * Challenge flows handled:
 * - Standard auth: Returns tokens immediately
 * - MFA required: Returns challenge, accepts code in same endpoint
 * - NEW_PASSWORD_REQUIRED: Admin-created users must set password
 * - Password expired: Force password change per policy
 *
 * @param params - Authentication parameters
 * @returns Auth tokens or challenge response
 * @throws User-friendly error message (never exposes system details)
 * @security NEVER log the password, code, or returned tokens
 */
export async function authenticate({
  username,
  password,
  code,
  newPassword,
  given_name,
  family_name,
  ssoProvider,
}: AuthenticateParams): Promise<CognitoAuthResponse> {
  const path = 'auth/login';

  const config = {
    method: 'post' as const,
    path,
    headers: {
      // WHY Basic Auth: Separates credentials from body, automatically redacted in logs,
      // follows HTTP standards. Backend immediately validates and discards.
      // btoa() creates base64 encoding required by Basic Auth specification.
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
    },
    body: {
      code, // MFA code if responding to MFA_REQUIRED challenge
      newPassword, // New password if responding to NEW_PASSWORD_REQUIRED
      givenName: given_name, // Profile completion for new users
      familyName: family_name,
      provider: ssoProvider, // Future: SSO integration
    },
  };

  const res = await getData<CognitoAuthResponse>({ ...config });

  return res;
}

/**
 * Logs out the user and invalidates their session.
 *
 * WHY backend logout instead of just clearing localStorage:
 * - Server-side session invalidation prevents token reuse if stolen
 * - Triggers audit log entry for security compliance
 * - Cleans up server-side session state and active device tracking
 * - Revokes refresh token to prevent automatic re-authentication
 *
 * Security considerations:
 * - Backend invalidates both access and refresh tokens
 * - Prevents session fixation attacks by generating new session on next login
 * - Audit trail maintained for compliance (GDPR, SOC2)
 *
 * @returns Logout confirmation
 * @security Frontend should clear localStorage tokens after successful response
 */
export async function logout(): Promise<{ success: boolean }> {
  const config = {
    method: 'post' as const,
    path: 'auth/logout',
  };

  // WHY POST instead of GET: Logout changes server state (token revocation),
  // GET requests should be idempotent. POST prevents accidental logout via
  // browser prefetch or image tags (CSRF protection).
  const res = await getData<{ success: boolean }>({ ...config });
  return res;
}

// ============================================================================
// User Registration
// ============================================================================

/**
 * Initiates user registration with AWS Cognito.
 *
 * WHY multi-step registration (signup → verify):
 * - Email verification required for account recovery and communication
 * - Prevents fake account creation and email typos
 * - Cognito enforces this pattern for security compliance
 * - Allows progressive disclosure (basic info first, profile later)
 *
 * Security patterns:
 * - Password in Authorization header, not body (prevents logging)
 * - Email uniqueness validated server-side (prevents enumeration)
 * - Marketing consent tracked separately for GDPR compliance
 * - Rate limiting applied server-side to prevent abuse
 *
 * @param data - Registration data
 * @returns Registration result with next steps
 * @throws User-friendly error (e.g., "Email already registered")
 * @security Password complexity enforced server-side, never trust client validation
 */
export async function signup(data: SignupParams): Promise<SignupResponse> {
  const config = {
    method: 'post' as const,
    path: 'auth/signup',
    headers: {
      // WHY Basic Auth for signup: Consistent with login pattern,
      // keeps passwords out of logs, follows security best practices.
      // Even though user doesn't exist yet, pattern consistency matters.
      Authorization: `Basic ${btoa(`${data.email}:${data.password}`)}`,
    },
    body: {
      email: data.email, // Becomes Cognito username
      given_name: data.given_name,
      family_name: data.family_name,
      recieve_marketing: data.recieve_marketing, // GDPR compliance tracking
    },
  };

  const res = await getData<SignupResponse>({ ...config });

  return res;
}

/**
 * Confirms user registration with verification code.
 *
 * WHY verification required: AWS Cognito enforces email verification to:
 * - Prevent spam accounts with fake emails
 * - Ensure account recovery channel is valid
 * - Meet compliance requirements (verified contact method)
 * - Reduce bounce rates for transactional emails
 *
 * Security considerations:
 * - Verification codes expire after 24 hours (Cognito default)
 * - Rate limited to prevent brute force (3 attempts per code)
 * - Generic errors returned to prevent user enumeration
 * - Code is 6 digits - balance between security and usability
 *
 * @param data - Verification data including code and username
 * @returns Confirmation result with auto-login tokens
 * @throws Generic "Invalid or expired code" message
 * @security Never reveal if user exists or specific failure reason
 */
export async function confirmSignup(data: ConfirmSignupParams): Promise<CognitoAuthResponse> {
  const config = {
    method: 'post' as const,
    path: 'auth/signup/verify',
    body: {
      ...data, // Contains username and verification code
    },
  };

  const res = await getData<CognitoAuthResponse>({ ...config });

  return res;
}

/**
 * Sends SMS verification for phone number - supports both signup and account update flows.
 *
 * TWO USE CASES:
 * 1. Mobile signup: User registers with phone number instead of email (sub and username undefined)
 * 2. Add phone to existing account: User adds phone for MFA/recovery (sub and username required)
 *
 * WHY dual-purpose function:
 * - Phone number used for SMS-based MFA (more secure than email)
 * - Required for account recovery when email compromised
 * - Regulatory compliance for financial services (KYC)
 * - Prevents SIM swapping by verifying ownership
 *
 * Security rationale:
 * - Phone number sanitized to prevent injection attacks
 * - Rate limited to prevent SMS bombing (cost protection)
 * - Sub (user ID) prevents unauthorized phone additions when adding to existing account
 * - Backend validates number format and carrier validity
 *
 * @param params - Verification parameters (sub and username optional for signup flow)
 * @returns SMS send confirmation
 * @security SMS codes expire in 5 minutes, max 3 attempts
 */
export async function sendMobileVerification({
  sub,
  phone_number,
  username,
}: {
  sub?: string;
  phone_number: string;
  username?: string;
}): Promise<{ success: boolean }> {
  const config = {
    method: 'post' as const,
    path: 'auth/signup/mobile',
    body: {
      // WHY conditional inclusion: For signup, sub and username don't exist yet
      // For adding phone to existing account, sub prevents unauthorized additions
      ...(sub && { sub }),
      // WHY sanitization: Remove spaces and trim to prevent formatting issues
      // Backend still validates E.164 format (+country_code number)
      phone_number: phone_number.replaceAll(' ', '').trim(),
      ...(username && { username }),
    },
  };

  const res = await getData<{ success: boolean }>({ ...config });

  return res;
}

/**
 * Resends the verification code for user signup
 *
 * @param params - Resend parameters
 * @returns Resend confirmation
 */
export async function resendVerification({
  username,
  attribute,
  forceEmailVerification,
}: {
  username: string;
  attribute?: string;
  forceEmailVerification?: boolean;
}): Promise<{ success: boolean }> {
  const config = {
    method: 'post' as const,
    path: 'auth/signup/verify/resend',
    body: {
      username,
      attribute,
      forceEmailVerification,
    },
  };

  const res = await getData<{ success: boolean }>({ ...config });

  return res;
}

// ============================================================================
// Attribute Management
// ============================================================================

/**
 * Confirms the change of username (email attribute) using a verification code
 *
 * @param params - Confirmation parameters
 * @returns Confirmation result
 */
export async function confirmAttributeChange({
  code,
  attribute,
}: ConfirmAttributeChangeParams): Promise<AttributeChangeResponse> {
  const config = {
    method: 'post' as const,
    path: 'user/attribute/verify',
    body: {
      code,
      attribute,
    },
  };

  const res = await getData<AttributeChangeResponse>({ ...config });

  return res;
}

/**
 * Resends the verification code when an attribute is updated
 *
 * @param params - Resend parameters
 * @returns Resend confirmation
 */
export async function resendAttriteConfirmCode({
  attribute,
}: ResendAttributeCodeParams): Promise<{ success: boolean }> {
  const config = {
    method: 'post' as const,
    path: 'user/attribute/resend-verification',
    body: {
      AttributeName: attribute, // Backend expects capitalized AttributeName
    },
  };

  const res = await getData<{ success: boolean }>({ ...config });

  return res;
}

// ============================================================================
// Password Reset
// ============================================================================

/**
 * Initiates the password reset flow
 *
 * @param params - Reset parameters
 * @returns Password reset confirmation
 */
export async function requestResetPassword({
  username,
}: ForgotPasswordParams): Promise<PasswordResetResponse> {
  const config = {
    method: 'post' as const,
    path: 'auth/request-password-reset',
    body: {
      username,
    },
  };

  const res = await getData<PasswordResetResponse>({ ...config });

  return res;
}

/**
 * Resets the user's password
 *
 * @param data - Reset data including code and new password
 * @returns Reset confirmation
 */
export async function resetPassword(data: ConfirmForgotPasswordParams): Promise<{ success: boolean }> {
  const { username, newPassword } = data;

  const config = {
    method: 'post' as const,
    path: 'auth/reset-password',
    headers: {
      Authorization: `Basic ${btoa(`${username}:${newPassword}`)}`,
    },
    body: {
      ...data,
    },
  };

  const res = await getData<{ success: boolean }>({ ...config });

  return res;
}

// ============================================================================
// User Management
// ============================================================================

/**
 * Updates the currently logged in user's attributes
 *
 * @param params - Update parameters
 * @returns Update confirmation
 */
export async function updateUser({
  data,
}: {
  data: UpdateUserAttributesParams['attributes'];
}): Promise<UserResponse> {
  const config = {
    method: 'patch' as const,
    path: 'user',
    body: data,
  };

  const res = await getData<UserResponse>({ ...config });

  return res;
}

/**
 * Specifically updates the email (username) of the current user
 *
 * @param params - Email update parameters
 * @returns Update confirmation
 */
export async function updateUserEmail({ email }: ChangeEmailParams): Promise<AttributeChangeResponse> {
  const config = {
    method: 'patch' as const,
    path: 'user/email',
    body: { email },
  };

  const res = await getData<AttributeChangeResponse>({ ...config });

  return res;
}

/**
 * Updates the mobile number of the current user
 *
 * @param params - Phone update parameters
 * @returns Update confirmation
 */
export async function updateUserMobileNumber({
  phone_number,
}: ChangePhoneParams): Promise<AttributeChangeResponse> {
  const config = {
    method: 'patch' as const,
    path: 'user/mobile-number',
    body: { phone_number },
  };

  const res = await getData<AttributeChangeResponse>({ ...config });

  return res;
}

/**
 * Gets the currently logged in user's data
 *
 * @returns User data or null if not authenticated
 */
export async function getUserData(): Promise<UserResponse | null> {
  const loggedIn = localStorage.getItem('accessToken');

  if (!loggedIn) return null;
  const config = {
    method: 'get' as const,
    path: 'user',
  };

  const res = await getData<UserResponse>({ ...config });

  return res;
}

/**
 * Deletes the current user
 *
 * @returns Deletion confirmation
 */
export async function deleteUser(): Promise<{ success: boolean }> {
  const config = {
    method: 'delete' as const,
    path: 'user',
  };

  const res = await getData<{ success: boolean }>({ ...config });

  return res;
}

// ============================================================================
// Multi-Factor Authentication (MFA)
// ============================================================================
// WHY MFA critical: Payment processing requires strong authentication.
// PCI DSS recommends MFA for all payment-related accounts.
// Reduces account takeover by 99.9% according to Microsoft research.

/**
 * Generates QR code and secret for TOTP-based MFA setup.
 *
 * WHY TOTP over SMS:
 * - SMS vulnerable to SIM swapping attacks
 * - TOTP works offline (no carrier dependency)
 * - Industry standard (RFC 6238) supported by Google Authenticator, Authy
 * - No per-message costs like SMS
 *
 * Security architecture:
 * - Secret generated server-side using cryptographically secure random
 * - QR code contains otpauth:// URI with issuer and account label
 * - Secret never logged or exposed after initial display
 * - User must verify code before MFA is fully enabled (prevent lockout)
 *
 * @returns QR code data URL and backup codes
 * @security Secret shown only once - user must save backup codes
 */
export async function generateMFA(): Promise<MFASetupResponse> {
  const config = {
    method: 'get' as const,
    path: 'auth/mfa/generate',
  };

  const res = await getData<MFASetupResponse>({ ...config });

  return res;
}

/**
 * Verifies TOTP code during MFA setup or login.
 *
 * WHY verification before enabling:
 * - Confirms user has correctly configured authenticator app
 * - Prevents permanent lockout from misconfiguration
 * - Validates time sync between client and server (30-second window)
 * - Creates backup codes for account recovery
 *
 * Security considerations:
 * - Code valid for 30 seconds only (single use)
 * - Rate limited to prevent brute force (1 attempt per 30 seconds)
 * - Generic error messages prevent timing attacks
 * - Successful verification triggers security event log
 *
 * @param params - Verification parameters
 * @returns Verification status and backup codes (first-time only)
 * @security Never log the code or indicate specific failure reason
 */
export async function verifyMFA({ code }: VerifyMFAParams): Promise<MFAVerification> {
  const config = {
    method: 'post' as const,
    path: 'auth/mfa/verify',
    body: { code }, // 6-digit TOTP code, rotates every 30 seconds
  };

  const res = await getData<MFAVerification>({ ...config });

  return res;
}

/**
 * Enables MFA requirement for user account.
 *
 * WHY enforce MFA:
 * - Required for admin accounts and payment processors (PCI compliance)
 * - Reduces support burden from account takeovers
 * - Insurance and liability requirements for financial data
 * - User trust and platform security reputation
 *
 * Implementation notes:
 * - Must call generateMFA and verifyMFA first
 * - Once enabled, all future logins require MFA code
 * - Backup codes provided for recovery scenarios
 * - Cannot be disabled without admin approval (high-value accounts)
 *
 * @param params - MFA configuration
 * @returns MFA activation confirmation
 * @security Triggers security notification to user's email
 */
export async function enableMFA({ type = 'TOTP' }: { type?: 'TOTP' | 'SMS' } = {}): Promise<{
  success: boolean;
}> {
  const config = {
    method: 'post' as const,
    path: 'auth/mfa/enable',
  };

  // WHY TOTP default: More secure than SMS, no carrier costs,
  // works internationally, not vulnerable to SIM swapping
  const res = await getData<{ success: boolean }>({ ...config });

  return res;
}

/**
 * Disables MFA for user account (requires recent authentication).
 *
 * WHY allow disable:
 * - User loses access to authenticator device
 * - Switching to new phone requires reconfiguration
 * - Support burden reduction for locked-out users
 *
 * Security safeguards:
 * - Requires recent password verification (within 5 minutes)
 * - Sends security alert to all verified email addresses
 * - Admin accounts may have policy preventing disable
 * - Audit log entry created with IP and device fingerprint
 *
 * @returns MFA deactivation confirmation
 * @security High-risk operation - implement additional verification in UI
 */
export async function disableMFA(): Promise<{ success: boolean }> {
  const config = {
    method: 'post' as const,
    path: 'auth/mfa/disable',
  };

  // WHY POST not DELETE: State change operation, needs CSRF protection,
  // may require additional verification parameters in future
  const res = await getData<{ success: boolean }>({ ...config });

  return res;
}

// ============================================================================
// Session Authentication (SSO Cookies)
// ============================================================================

/**
 * Authenticates using SSO session cookie
 *
 * @param params - Session parameters
 * @returns Session authentication result
 */
export async function sessionLogin({
  name,
  value,
}: {
  name: string;
  value: string;
}): Promise<CognitoAuthResponse> {
  const config = {
    method: 'post' as const,
    path: 'auth/session',
    headers: {
      Authorization: value,
      'token-name': name,
    },
  };

  const res = await getData<CognitoAuthResponse>({ ...config });

  return res;
}
